import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell, ReferenceLine, ScatterChart, Scatter, ZAxis, AreaChart, Area, ReferenceArea, Customized } from 'recharts';
import { NavBar } from '../components/NavBar';
import { StatCard } from '../components/StatCard';
import { SearchBar } from '../components/SearchBar';
import { ExportButton } from '../components/ExportButton';
import { Badge } from '../components/SubscriptionAppBadge';
import { TableFilter } from '../components/TableFilter';
import { Package, Plus, ArrowUp, ArrowDown, Minus, ChevronDown, Download, Info, Lightbulb, TrendingUp, ArrowUpRight, ArrowRight } from 'lucide-react';
const tabs = ['Stock Forecast', 'Product Performance', 'Recurring Revenue', 'Retention Health', 'Wine Journey'] as const;
type Tab = (typeof tabs)[number];
const timeHorizons = ['Next 7 Days', 'Next 30 Days', 'Next 60 Days', 'Next 90 Days'] as const;
type TimeHorizon = (typeof timeHorizons)[number];
interface ProductData {
  name: string;
  variant: string;
  sku: string;
  baseRequired: number;
  stock: number;
}
const baseProducts: ProductData[] = [{
  name: '2023 Reserve Cabernet',
  variant: '750ml',
  sku: '23RCAB-750',
  baseRequired: 89,
  stock: 45
}, {
  name: '2024 Sparkling Brut',
  variant: '750ml',
  sku: '24SPB-750',
  baseRequired: 34,
  stock: 0
}, {
  name: '2023 Rosé',
  variant: '750ml',
  sku: '23RSE-750',
  baseRequired: 42,
  stock: 15
}, {
  name: '2025 Chardonnay',
  variant: '750ml',
  sku: '25CHD3-750',
  baseRequired: 120,
  stock: 130
}, {
  name: '2024 Sauvignon Blanc',
  variant: '750ml',
  sku: '24SB2-750',
  baseRequired: 95,
  stock: 100
}, {
  name: '2024 Shiraz',
  variant: '750ml',
  sku: '24SHZ9-750',
  baseRequired: 156,
  stock: 200
}, {
  name: '2024 Pinot Noir',
  variant: '750ml',
  sku: '24PN5-750',
  baseRequired: 67,
  stock: 180
}, {
  name: '2023 Merlot',
  variant: '750ml',
  sku: '23MRL-750',
  baseRequired: 78,
  stock: 250
}];
// Product Performance Data Types & Constants
type SalesMode = 'subscription' | 'all';
interface PerfProduct {
  name: string;
  sku: string;
  category: string;
  unitsSold: number;
  revenue: number;
  avgPrice: number;
  trend: number | null; // percentage, positive = up, negative = down, null = no trend
  activeSubs: number | null; // null for non-subscription products
  allUnits: number;
  allRevenue: number;
}
const perfProducts: PerfProduct[] = [{
  name: '2024 Shiraz',
  sku: '24SHZ9',
  category: 'Red',
  unitsSold: 156,
  revenue: 4680,
  avgPrice: 30,
  trend: 12,
  activeSubs: 89,
  allUnits: 420,
  allRevenue: 12600
}, {
  name: '2025 Chardonnay',
  sku: '25CHD3',
  category: 'White',
  unitsSold: 120,
  revenue: 3600,
  avgPrice: 30,
  trend: 8,
  activeSubs: 72,
  allUnits: 310,
  allRevenue: 9300
}, {
  name: '2024 Sauvignon Blanc',
  sku: '24SB2',
  category: 'White',
  unitsSold: 95,
  revenue: 2375,
  avgPrice: 25,
  trend: -3,
  activeSubs: 58,
  allUnits: 260,
  allRevenue: 6500
}, {
  name: '2023 Reserve Cabernet',
  sku: '23RCAB',
  category: 'Red',
  unitsSold: 89,
  revenue: 3560,
  avgPrice: 40,
  trend: 22,
  activeSubs: 45,
  allUnits: 195,
  allRevenue: 7800
}, {
  name: '2023 Merlot',
  sku: '23MRL',
  category: 'Red',
  unitsSold: 78,
  revenue: 2340,
  avgPrice: 30,
  trend: -8,
  activeSubs: 38,
  allUnits: 210,
  allRevenue: 6300
}, {
  name: '2024 Pinot Noir',
  sku: '24PN5',
  category: 'Red',
  unitsSold: 67,
  revenue: 2345,
  avgPrice: 35,
  trend: -5,
  activeSubs: 41,
  allUnits: 180,
  allRevenue: 6300
}, {
  name: '2023 Rosé',
  sku: '23RSE',
  category: 'Rosé',
  unitsSold: 42,
  revenue: 1050,
  avgPrice: 25,
  trend: 15,
  activeSubs: 28,
  allUnits: 115,
  allRevenue: 2875
}, {
  name: '2024 Sparkling Brut',
  sku: '24SPB',
  category: 'Sparkling',
  unitsSold: 34,
  revenue: 1190,
  avgPrice: 35,
  trend: 45,
  activeSubs: 22,
  allUnits: 88,
  allRevenue: 3080
}];
const allSalesOnlyProducts: PerfProduct[] = [{
  name: "Oliver's Olive Oil",
  sku: 'OLIVEOIL',
  category: 'Pantry',
  unitsSold: 0,
  revenue: 0,
  avgPrice: 29.1,
  trend: null,
  activeSubs: null,
  allUnits: 210,
  allRevenue: 6111
}, {
  name: 'Gift Voucher $50',
  sku: 'GIFT50',
  category: 'Gifts',
  unitsSold: 0,
  revenue: 0,
  avgPrice: 50,
  trend: null,
  activeSubs: null,
  allUnits: 45,
  allRevenue: 2250
}];
const subscriptionCategories = [{
  name: 'All Products',
  count: null
}, {
  name: 'Red',
  count: 16
}, {
  name: 'White',
  count: 12
}, {
  name: 'Sparkling',
  count: 4
}, {
  name: 'Rosé',
  count: 3
}, {
  name: 'Fortified',
  count: 2
}, {
  name: 'Mixed Packs',
  count: 3
}, {
  name: 'Accessories',
  count: 2
}];
const allSalesCategories = [...subscriptionCategories, {
  name: 'Gifts',
  count: 16
}, {
  name: 'Pantry',
  count: 4
}];
// Recurring Revenue Data Types & Constants
const revenueHorizons = ['Next 7 Days', 'Next 30 Days', 'Next 3 Months', 'Next 12 Months'] as const;
type RevenueHorizon = (typeof revenueHorizons)[number];
interface RevenueProduct {
  name: string;
  subscriptionCount: number;
  baseForecastUnits: number;
  baseForecastRevenue: number;
  avgRecurring: number;
  avgLTV: number;
}
const retentionScoreData = [{
  month: 'Jan',
  score: 65
}, {
  month: 'Feb',
  score: 67
}, {
  month: 'Mar',
  score: 68
}, {
  month: 'Apr',
  score: 70
}, {
  month: 'May',
  score: 71
}, {
  month: 'Jun',
  score: 72
}, {
  month: 'Jul',
  score: 73
}, {
  month: 'Aug',
  score: 74
}, {
  month: 'Sep',
  score: 74
}, {
  month: 'Oct',
  score: 76
}, {
  month: 'Nov',
  score: 77
}, {
  month: 'Dec',
  score: 78
}];
const tenureDistributionData = [{
  range: '0-3m',
  count: 28
}, {
  range: '3-6m',
  count: 42
}, {
  range: '6-12m',
  count: 95
}, {
  range: '1-2y',
  count: 88
}, {
  range: '2-3y',
  count: 55
}, {
  range: '3+y',
  count: 42
}];
const waterfallChartData = [{
  name: 'Active Start',
  placeholder: 328,
  value: 14,
  color: '#0EA5E9',
  label: '342'
}, {
  name: 'New',
  placeholder: 342,
  value: 18,
  color: '#10B981',
  label: '+18'
}, {
  name: 'Reactivated',
  placeholder: 360,
  value: 4,
  color: '#10B981',
  label: '+4'
}, {
  name: 'Cancelled',
  placeholder: 350,
  value: 14,
  color: '#EF4444',
  label: '-14'
}, {
  name: 'Expired',
  placeholder: 348,
  value: 2,
  color: '#EF4444',
  label: '-2'
}, {
  name: 'Active End',
  placeholder: 328,
  value: 20,
  color: '#0EA5E9',
  label: '348'
}];
const retentionCurveData = [{
  month: 'M0',
  club: 100,
  industry: 100
}, {
  month: 'M3',
  club: 92,
  industry: 85
}, {
  month: 'M6',
  club: 85,
  industry: 70
}, {
  month: 'M9',
  club: 78,
  industry: 62
}, {
  month: 'M12',
  club: 72,
  industry: 55
}, {
  month: 'M15',
  club: 68,
  industry: 48
}, {
  month: 'M18',
  club: 64,
  industry: 44
}, {
  month: 'M21',
  club: 61,
  industry: 40
}, {
  month: 'M24',
  club: 58,
  industry: 38
}];
// Wine Journey Data
const productLifecycleData = [{
  name: '2024 Rosé',
  tenure: 2,
  repeatRate: 15,
  units: 115,
  color: '#F472B6'
}, {
  name: '2024 Sauv Blanc',
  tenure: 3,
  repeatRate: 25,
  units: 260,
  color: '#34D399'
}, {
  name: '2023 Shiraz',
  tenure: 8,
  repeatRate: 65,
  units: 420,
  color: '#0EA5E9'
}, {
  name: '2023 Reserve Cab',
  tenure: 14,
  repeatRate: 72,
  units: 195,
  color: '#8B5CF6'
}, {
  name: '2024 Pinot Noir',
  tenure: 6,
  repeatRate: 45,
  units: 180,
  color: '#F59E0B'
}, {
  name: '2023 Sparkling',
  tenure: 1,
  repeatRate: 8,
  units: 88,
  color: '#94A3B8'
}];
const swapDropData = [{
  name: '2024 Pinot Noir',
  count: 34,
  type: 'in'
}, {
  name: '2023 Shiraz',
  count: 28,
  type: 'in'
}, {
  name: '2024 Rosé',
  count: 22,
  type: 'out'
}, {
  name: '2023 Merlot',
  count: 18,
  type: 'out'
}, {
  name: '2024 Sauv Blanc',
  count: 15,
  type: 'in'
}, {
  name: '2023 Sparkling',
  count: 12,
  type: 'out'
}];
const varietalDiversityData = [{
  month: 'Jan',
  shiraz: 32,
  cabernet: 22,
  pinotNoir: 12,
  chardonnay: 10,
  sauvBlanc: 8,
  rose: 6,
  sparkling: 5,
  other: 5
}, {
  month: 'Feb',
  shiraz: 30,
  cabernet: 21,
  pinotNoir: 13,
  chardonnay: 11,
  sauvBlanc: 9,
  rose: 6,
  sparkling: 5,
  other: 5
}, {
  month: 'Mar',
  shiraz: 29,
  cabernet: 20,
  pinotNoir: 14,
  chardonnay: 11,
  sauvBlanc: 9,
  rose: 7,
  sparkling: 5,
  other: 5
}, {
  month: 'Apr',
  shiraz: 28,
  cabernet: 19,
  pinotNoir: 14,
  chardonnay: 12,
  sauvBlanc: 10,
  rose: 7,
  sparkling: 5,
  other: 5
}, {
  month: 'May',
  shiraz: 27,
  cabernet: 18,
  pinotNoir: 15,
  chardonnay: 12,
  sauvBlanc: 10,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Jun',
  shiraz: 26,
  cabernet: 18,
  pinotNoir: 15,
  chardonnay: 12,
  sauvBlanc: 11,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Jul',
  shiraz: 25,
  cabernet: 17,
  pinotNoir: 16,
  chardonnay: 13,
  sauvBlanc: 11,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Aug',
  shiraz: 25,
  cabernet: 17,
  pinotNoir: 16,
  chardonnay: 13,
  sauvBlanc: 11,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Sep',
  shiraz: 24,
  cabernet: 16,
  pinotNoir: 16,
  chardonnay: 14,
  sauvBlanc: 12,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Oct',
  shiraz: 23,
  cabernet: 16,
  pinotNoir: 17,
  chardonnay: 14,
  sauvBlanc: 12,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Nov',
  shiraz: 22,
  cabernet: 15,
  pinotNoir: 17,
  chardonnay: 14,
  sauvBlanc: 13,
  rose: 9,
  sparkling: 5,
  other: 5
}, {
  month: 'Dec',
  shiraz: 22,
  cabernet: 15,
  pinotNoir: 17,
  chardonnay: 14,
  sauvBlanc: 13,
  rose: 9,
  sparkling: 5,
  other: 5
}];
const revenueProducts: RevenueProduct[] = [{
  name: '2022 Shiraz',
  subscriptionCount: 2768,
  baseForecastUnits: 3200,
  baseForecastRevenue: 96000,
  avgRecurring: 67.61,
  avgLTV: 90.15
}, {
  name: '2024 Grenache',
  subscriptionCount: 2520,
  baseForecastUnits: 2800,
  baseForecastRevenue: 70000,
  avgRecurring: 50.31,
  avgLTV: 72.69
}, {
  name: '2025 Fiano',
  subscriptionCount: 2243,
  baseForecastUnits: 2500,
  baseForecastRevenue: 62500,
  avgRecurring: 50.6,
  avgLTV: 59.45
}, {
  name: "2024 Brioni's Blend",
  subscriptionCount: 1744,
  baseForecastUnits: 1900,
  baseForecastRevenue: 47500,
  avgRecurring: 50.68,
  avgLTV: 87.34
}, {
  name: '2025 Vermentino',
  subscriptionCount: 1545,
  baseForecastUnits: 1700,
  baseForecastRevenue: 42500,
  avgRecurring: 42.56,
  avgLTV: 27.33
}, {
  name: '2025 Chica Mencia Rosé',
  subscriptionCount: 1338,
  baseForecastUnits: 1500,
  baseForecastRevenue: 37500,
  avgRecurring: 43.97,
  avgLTV: 22.87
}, {
  name: '2021 HJ Shiraz',
  subscriptionCount: 1056,
  baseForecastUnits: 1100,
  baseForecastRevenue: 27500,
  avgRecurring: 128.82,
  avgLTV: 216.45
}, {
  name: '2024 Tempranillo',
  subscriptionCount: 1008,
  baseForecastUnits: 1100,
  baseForecastRevenue: 27500,
  avgRecurring: 43.73,
  avgLTV: 68.08
}];
export function AnalyticsPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('Stock Forecast');
  const [timeHorizon, setTimeHorizon] = useState<TimeHorizon>('Next 30 Days');
  const [searchQuery, setSearchQuery] = useState('');
  // Product Performance State
  const [salesMode, setSalesMode] = useState<SalesMode>('subscription');
  const [dateRange, setDateRange] = useState('Last 30 Days');
  const [selectedCategory, setSelectedCategory] = useState('All Products');
  const [perfSearchQuery, setPerfSearchQuery] = useState('');
  // Recurring Revenue State
  const [revenueHorizon, setRevenueHorizon] = useState<RevenueHorizon>('Next 30 Days');
  // Calculate factor based on time horizon
  const factor = useMemo(() => {
    switch (timeHorizon) {
      case 'Next 7 Days':
        return 0.4;
      case 'Next 30 Days':
        return 1.0;
      case 'Next 60 Days':
        return 1.8;
      case 'Next 90 Days':
        return 2.5;
      default:
        return 1.0;
    }
  }, [timeHorizon]);
  // Process data for Stock Forecast
  const processedData = useMemo(() => {
    return baseProducts.map(product => {
      const required = Math.round(product.baseRequired * factor);
      const delta = product.stock - required;
      let status: 'In Stock' | 'Low Stock' | 'Out of Stock' = 'In Stock';
      if (delta < 0) {
        status = 'Out of Stock';
      } else if (delta < required * 0.2) {
        status = 'Low Stock';
      }
      return {
        ...product,
        required,
        delta,
        status
      };
    }).filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.sku.toLowerCase().includes(searchQuery.toLowerCase())).sort((a, b) => {
      const statusOrder = {
        'Out of Stock': 0,
        'Low Stock': 1,
        'In Stock': 2
      };
      if (statusOrder[a.status] !== statusOrder[b.status]) {
        return statusOrder[a.status] - statusOrder[b.status];
      }
      return a.delta - b.delta;
    });
  }, [factor, searchQuery]);
  // Calculate stats for Stock Forecast
  const stats = useMemo(() => {
    const totalUnits = Math.round(847 * factor);
    const estimatedRevenue = Math.round(18420 * factor);
    const subscriptionsDue = Math.round(156 * factor);
    return {
      totalUnits,
      productsNeeded: 24,
      estimatedRevenue,
      subscriptionsDue
    };
  }, [factor]);
  // Process data for Product Performance
  const perfData = useMemo(() => {
    let products = [...perfProducts];
    if (salesMode === 'all') {
      products = [...products, ...allSalesOnlyProducts];
    }
    return products.filter(product => {
      const matchesCategory = selectedCategory === 'All Products' || product.category === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(perfSearchQuery.toLowerCase()) || product.sku.toLowerCase().includes(perfSearchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    }).sort((a, b) => {
      const revA = salesMode === 'all' ? a.allRevenue : a.revenue;
      const revB = salesMode === 'all' ? b.allRevenue : b.revenue;
      return revB - revA;
    });
  }, [salesMode, selectedCategory, perfSearchQuery]);
  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'In Stock':
        return 'success';
      case 'Low Stock':
        return 'warning';
      case 'Out of Stock':
        return 'error';
      default:
        return 'default';
    }
  };
  // Generate Chart Data for Recurring Revenue
  const chartData = useMemo(() => {
    const data = [];
    const today = new Date();
    if (revenueHorizon === 'Next 7 Days' || revenueHorizon === 'Next 30 Days') {
      const days = revenueHorizon === 'Next 7 Days' ? 7 : 30;
      for (let i = 0; i < days; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        const dayOfMonth = date.getDate();
        const isSpikeDay = dayOfMonth === 1 || dayOfMonth === 15;
        // Base values
        let orders = Math.floor(Math.random() * 6) + 3; // 3-8
        let revenue = Math.floor(Math.random() * 1200) + 600; // 600-1800
        // Spike values
        if (isSpikeDay) {
          orders = Math.floor(Math.random() * 7) + 12; // 12-18
          revenue = Math.floor(Math.random() * 1500) + 2500; // 2500-4000
        }
        data.push({
          date: date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
          }),
          orders,
          revenue
        });
      }
    } else if (revenueHorizon === 'Next 3 Months') {
      for (let i = 0; i < 12; i++) {
        const orders = Math.floor(Math.random() * 20) + 40; // 40-60 per week
        const revenue = Math.floor(Math.random() * 4000) + 8000; // 8000-12000
        data.push({
          date: `Week ${i + 1}`,
          orders,
          revenue
        });
      }
    } else {
      // Next 12 Months
      for (let i = 0; i < 12; i++) {
        const date = new Date(today);
        date.setMonth(today.getMonth() + i);
        const orders = Math.floor(Math.random() * 50) + 150; // 150-200 per month
        const revenue = Math.floor(Math.random() * 10000) + 35000; // 35000-45000
        data.push({
          date: date.toLocaleDateString('en-US', {
            month: 'short',
            year: '2-digit'
          }),
          orders,
          revenue
        });
      }
    }
    return data;
  }, [revenueHorizon]);
  // Calculate Stats for Recurring Revenue
  const revenueStats = useMemo(() => {
    let forecastRevenue = 38330;
    let renewalOrders = 172;
    let avgRenewalAmount = 222.85;
    if (revenueHorizon === 'Next 7 Days') {
      forecastRevenue = 8950;
      renewalOrders = 42;
      avgRenewalAmount = 213.1;
    } else if (revenueHorizon === 'Next 3 Months') {
      forecastRevenue = 118500;
      renewalOrders = 540;
      avgRenewalAmount = 219.44;
    } else if (revenueHorizon === 'Next 12 Months') {
      forecastRevenue = 485000;
      renewalOrders = 2150;
      avgRenewalAmount = 225.58;
    }
    return {
      forecastRevenue,
      renewalOrders,
      avgRenewalAmount
    };
  }, [revenueHorizon]);
  // Calculate Table Data for Recurring Revenue
  const revenueTableData = useMemo(() => {
    let multiplier = 1;
    if (revenueHorizon === 'Next 7 Days') multiplier = 0.23;
    if (revenueHorizon === 'Next 3 Months') multiplier = 3;
    if (revenueHorizon === 'Next 12 Months') multiplier = 12;
    return revenueProducts.map(product => ({
      ...product,
      forecastUnits: Math.round(product.baseForecastUnits * multiplier),
      forecastRevenue: Math.round(product.baseForecastRevenue * multiplier)
    }));
  }, [revenueHorizon]);
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-4370">
      <div className="mb-6" data-id="element-4371">
        <NavBar tabs={[]} title="Analytics" subtitle="Insights across stock, products, revenue, retention, and wine journey" data-id="element-4372" />
      </div>

      {/* Tab Bar */}
      <div className="border-b border-[#E2E8F0] mb-6" data-id="element-4373">
        <div className="flex gap-6" data-id="element-4374">
          {tabs.map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-sm font-medium relative transition-colors ${activeTab === tab ? 'text-[#0EA5E9]' : 'text-[#475569] hover:text-[#0F172A]'}`} data-id="element-4375">
              {tab}
              {activeTab === tab && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#0EA5E9] rounded-t-full" data-id="element-4376" />}
            </button>)}
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'Stock Forecast' ? <div className="space-y-6" data-id="element-4377">
          {/* Time Horizon Selector */}
          <div className="flex justify-start" data-id="element-4378">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-4379">
              {timeHorizons.map(horizon => <button key={horizon} onClick={() => setTimeHorizon(horizon)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${timeHorizon === horizon ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-4380">
                  {horizon}
                </button>)}
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-4381">
            <StatCard label="Total Units Required" value={stats.totalUnits.toLocaleString()} data-id="element-4382" />
            <StatCard label="Products Needed" value={stats.productsNeeded} data-id="element-4383" />
            <StatCard label="Estimated Revenue" value={`$${stats.estimatedRevenue.toLocaleString()}`} data-id="element-4384" />
            <StatCard label="Subscriptions Due" value={stats.subscriptionsDue.toLocaleString()} data-id="element-4385" />
          </div>

          {/* Actions Bar */}
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm" data-id="element-4386">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-4387">
              <div className="w-full lg:w-80 flex-shrink-0" data-id="element-4388">
                <SearchBar placeholder="Search products..." value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-4389" />
              </div>
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-4390">
                <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-4391" />
                <TableFilter filters={[]} // Placeholder for now
            onFilterChange={() => {}} onFilterRemove={() => {}} onToggleDropdown={() => {}} addedFilterIds={new Set()} onAddFilter={() => {}} renderOnlyButton={true} data-id="element-4392" />
              </div>
            </div>
          </div>

          {/* Stock Forecast Table */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-sm" data-id="element-4393">
            <div className="overflow-x-auto" data-id="element-4394">
              <table className="w-full" data-id="element-4395">
                <thead className="bg-[#F1F5F9]" data-id="element-4396">
                  <tr data-id="element-4397">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4398">
                      Product
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4399">
                      Variant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4400">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4401">
                      Units Required
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4402">
                      Current Stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4403">
                      Stock Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4404">
                      Surplus/Shortfall
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4405">
                  {processedData.map(product => <tr key={product.sku} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-4406">
                      <td className="px-6 py-4" data-id="element-4407">
                        <div className="flex items-center gap-3" data-id="element-4408">
                          <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0 text-[#94A3B8]" data-id="element-4409">
                            <Package className="w-4 h-4" data-id="element-4410" />
                          </div>
                          <button onClick={() => navigate('/operations/product-replace', {
                      state: {
                        preSelectedProduct: product.sku
                      }
                    })} className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] hover:underline text-left" data-id="element-4411">
                            {product.name}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569]" data-id="element-4412">
                        {product.variant}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] font-mono" data-id="element-4413">
                        {product.sku}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] font-medium text-right" data-id="element-4414">
                        {product.required}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-4415">
                        {product.stock}
                      </td>
                      <td className="px-6 py-4" data-id="element-4416">
                        <Badge variant={getStatusBadgeVariant(product.status)} data-id="element-4417">
                          {product.status}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-4418">
                        <span className={`text-sm font-medium ${product.delta >= 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-id="element-4419">
                          {product.delta > 0 ? '+' : ''}
                          {product.delta}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div> : activeTab === 'Product Performance' ? <div className="space-y-6" data-id="element-4420">
          {/* Controls Row */}
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-4421">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-4422">
              <button onClick={() => setSalesMode('subscription')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${salesMode === 'subscription' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-4423">
                Subscription Sales
              </button>
              <button onClick={() => setSalesMode('all')} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${salesMode === 'all' ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-4424">
                All Sales
              </button>
            </div>

            <div className="relative" data-id="element-4425">
              <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="appearance-none bg-white border border-[#CBD5E1] rounded-md pl-4 pr-10 py-1.5 text-sm text-[#0F172A] focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] cursor-pointer" data-id="element-4426">
                <option data-id="element-4427">Last 7 Days</option>
                <option data-id="element-4428">Last 30 Days</option>
                <option data-id="element-4429">Last 90 Days</option>
                <option data-id="element-4430">This Year</option>
                <option data-id="element-4431">Custom</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" data-id="element-4432" />
            </div>
          </div>

          {/* Stat Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-4433">
            {salesMode === 'subscription' ? <>
                <StatCard label="Units Sold" value="847" data-id="element-4434" />
                <StatCard label="Revenue" value="$18,420" data-id="element-4435" />
                <StatCard label="Unique Products" value="24" data-id="element-4436" />
                <StatCard label="Active Subscriptions" value="156" data-id="element-4437" />
              </> : <>
                <StatCard label="Units Sold" value="2,340" data-id="element-4438" />
                <StatCard label="Revenue" value="$52,600" data-id="element-4439" />
                <StatCard label="Unique Products" value="42" data-id="element-4440" />
                <StatCard label="Orders" value="389" data-id="element-4441" />
              </>}
          </div>

          {/* Main Content Area */}
          <div className="flex flex-col lg:flex-row gap-6" data-id="element-4442">
            {/* Category Sidebar */}
            <div className="w-full lg:w-64 flex-shrink-0 bg-white rounded-lg border border-[#E2E8F0] shadow-sm overflow-hidden h-fit" data-id="element-4443">
              <div className="px-4 py-3 bg-[#F8FAFC] border-b border-[#E2E8F0]" data-id="element-4444">
                <h3 className="text-[12px] font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4445">
                  Categories
                </h3>
              </div>
              <div className="p-2" data-id="element-4446">
                {(salesMode === 'subscription' ? subscriptionCategories : allSalesCategories).map(category => <button key={category.name} onClick={() => setSelectedCategory(category.name)} className={`w-full px-4 py-2.5 text-left text-[14px] flex justify-between items-center rounded-md transition-colors ${selectedCategory === category.name ? 'bg-[#E0F2FE] text-[#0EA5E9] font-medium' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-4447">
                    <span data-id="element-4448">{category.name}</span>
                    {category.count !== null && <span className={`text-[12px] ${selectedCategory === category.name ? 'text-[#0EA5E9]' : 'text-[#94A3B8]'}`} data-id="element-4449">
                        {category.count}
                      </span>}
                  </button>)}
              </div>
            </div>

            {/* Right Content */}
            <div className="flex-1 min-w-0" data-id="element-4450">
              {/* Actions Bar */}
              <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 shadow-sm mb-6" data-id="element-4451">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4452">
                  <div className="w-full sm:w-80 flex-shrink-0" data-id="element-4453">
                    <SearchBar placeholder="Search products..." value={perfSearchQuery} onChange={setPerfSearchQuery} className="w-full" data-id="element-4454" />
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-4455">
                    <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-4456" />
                    <TableFilter filters={[]} onFilterChange={() => {}} onFilterRemove={() => {}} onToggleDropdown={() => {}} addedFilterIds={new Set()} onAddFilter={() => {}} renderOnlyButton={true} data-id="element-4457" />
                  </div>
                </div>
              </div>

              {/* Product Table */}
              <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-sm" data-id="element-4458">
                <div className="overflow-x-auto" data-id="element-4459">
                  <table className="w-full" data-id="element-4460">
                    <thead className="bg-[#F1F5F9]" data-id="element-4461">
                      <tr data-id="element-4462">
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4463">
                          Product
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4464">
                          SKU
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4465">
                          Units Sold
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4466">
                          Revenue
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4467">
                          Avg Price
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4468">
                          Trend
                        </th>
                        {salesMode === 'subscription' && <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4469">
                            Active Subs
                          </th>}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4470">
                      {perfData.map(product => <tr key={product.sku} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-4471">
                          <td className="px-6 py-4" data-id="element-4472">
                            <div className="flex items-center gap-3" data-id="element-4473">
                              <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0 text-[#94A3B8]" data-id="element-4474">
                                <Package className="w-4 h-4" data-id="element-4475" />
                              </div>
                              <span className="text-sm font-medium text-[#0F172A]" data-id="element-4476">
                                {product.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569] font-mono" data-id="element-4477">
                            {product.sku}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0F172A] text-right" data-id="element-4478">
                            {salesMode === 'all' ? product.allUnits.toLocaleString() : product.unitsSold.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#0F172A] text-right font-medium" data-id="element-4479">
                            $
                            {salesMode === 'all' ? product.allRevenue.toLocaleString() : product.revenue.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-4480">
                            ${product.avgPrice.toFixed(2)}
                          </td>
                          <td className="px-6 py-4" data-id="element-4481">
                            {product.trend !== null ? <div className={`flex items-center gap-1 text-sm font-medium ${product.trend > 0 ? 'text-[#10B981]' : 'text-[#EF4444]'}`} data-id="element-4482">
                                {product.trend > 0 ? <ArrowUp className="w-4 h-4" data-id="element-4483" /> : <ArrowDown className="w-4 h-4" data-id="element-4484" />}
                                {Math.abs(product.trend)}%
                              </div> : <div className="flex items-center gap-1 text-sm font-medium text-[#94A3B8]" data-id="element-4485">
                                <Minus className="w-4 h-4" data-id="element-4486" />
                              </div>}
                          </td>
                          {salesMode === 'subscription' && <td className="px-6 py-4 text-sm text-[#0F172A] text-right" data-id="element-4487">
                              {product.activeSubs}
                            </td>}
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div> : activeTab === 'Recurring Revenue' ? <div className="space-y-6" data-id="element-4488">
          {/* Controls Row */}
          <div className="flex justify-between items-center" data-id="element-4489">
            <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-4490">
              {revenueHorizons.map(horizon => <button key={horizon} onClick={() => setRevenueHorizon(horizon)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${revenueHorizon === horizon ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-4491">
                  {horizon}
                </button>)}
            </div>
            <ExportButton onExport={async type => console.log(`Exporting ${type}`)} data-id="element-4492" />
          </div>

          <div className="flex flex-col lg:flex-row gap-6" data-id="element-4493">
            {/* Left Column: Stats */}
            <div className="w-full lg:w-64 flex-shrink-0 space-y-4" data-id="element-4494">
              <StatCard label="Forecast Revenue" value={`$${revenueStats.forecastRevenue.toLocaleString()}`} secondaryValue="renewal income in this period" data-id="element-4495" />
              <StatCard label="Renewal Orders" value={revenueStats.renewalOrders.toLocaleString()} secondaryValue="subscription renewals due" data-id="element-4496" />
              <StatCard label="Avg Renewal Amount" value={`$${revenueStats.avgRenewalAmount.toFixed(2)}`} secondaryValue="average order value" data-id="element-4497" />
            </div>

            {/* Right Column: Chart */}
            <div className="flex-1 bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm min-h-[400px]" data-id="element-4498">
              <h3 className="text-[16px] font-semibold text-[#0F172A] mb-6" data-id="element-4499">
                Revenue Forecast
              </h3>
              <div className="h-[320px] w-full" data-id="element-4500">
                <ResponsiveContainer width="100%" height="100%" data-id="element-4501">
                  <LineChart data={chartData} margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5
              }} data-id="element-4502">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4503" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} dy={10} data-id="element-4504" />
                    <YAxis yAxisId="left" orientation="left" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} label={{
                  value: 'Orders',
                  angle: -90,
                  position: 'insideLeft',
                  fill: '#64748B',
                  fontSize: 12
                }} data-id="element-4505" />
                    <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} tickFormatter={value => `$${value}`} label={{
                  value: 'Revenue',
                  angle: 90,
                  position: 'insideRight',
                  fill: '#64748B',
                  fontSize: 12
                }} data-id="element-4506" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} data-id="element-4507" />
                    <Legend data-id="element-4508" />
                    <Line yAxisId="left" type="monotone" dataKey="orders" name="Renewal Orders" stroke="#10B981" strokeWidth={2} dot={false} activeDot={{
                  r: 6
                }} data-id="element-4509" />
                    <Line yAxisId="right" type="monotone" dataKey="revenue" name="Revenue" stroke="#F59E0B" strokeWidth={2} dot={false} activeDot={{
                  r: 6
                }} data-id="element-4510" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Product Breakdown Table */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden shadow-sm" data-id="element-4511">
            <div className="px-6 py-4 border-b border-[#E2E8F0]" data-id="element-4512">
              <h3 className="text-[16px] font-semibold text-[#0F172A]" data-id="element-4513">
                Product Breakdown
              </h3>
            </div>
            <div className="overflow-x-auto" data-id="element-4514">
              <table className="w-full" data-id="element-4515">
                <thead className="bg-[#F1F5F9]" data-id="element-4516">
                  <tr data-id="element-4517">
                    <th className="px-6 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4518">
                      Product
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider group cursor-help" data-id="element-4519">
                      <div className="flex items-center justify-end gap-1" data-id="element-4520">
                        Subscription Count
                        <div className="relative group" title="Number of active subscriptions that include this product" data-id="element-4521">
                          <Info className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-4522" />
                        </div>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4523">
                      Forecast Units
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4524">
                      Forecast Revenue
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4525">
                      <div className="flex items-center justify-end gap-1" data-id="element-4526">
                        Avg Recurring Line Total
                        <div className="relative group" title="Average revenue generated per subscription per renewal for this product" data-id="element-4527">
                          <Info className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-4528" />
                        </div>
                      </div>
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-[#64748B] uppercase tracking-wider" data-id="element-4529">
                      <div className="flex items-center justify-end gap-1" data-id="element-4530">
                        Avg Lifetime Value
                        <div className="relative group" title="Average total revenue this product has generated per subscriber across all their orders" data-id="element-4531">
                          <Info className="w-3.5 h-3.5 text-[#94A3B8]" data-id="element-4532" />
                        </div>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-4533">
                  {revenueTableData.map(product => <tr key={product.name} className="hover:bg-[#F8FAFC] transition-colors" data-id="element-4534">
                      <td className="px-6 py-4" data-id="element-4535">
                        <div className="flex items-center gap-3" data-id="element-4536">
                          <div className="w-8 h-8 rounded bg-[#F1F5F9] flex items-center justify-center flex-shrink-0 text-[#94A3B8]" data-id="element-4537">
                            <Package className="w-4 h-4" data-id="element-4538" />
                          </div>
                          <span className="text-sm font-medium text-[#0F172A]" data-id="element-4539">
                            {product.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right font-mono" data-id="element-4540">
                        {product.subscriptionCount.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] text-right" data-id="element-4541">
                        {product.forecastUnits.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#0F172A] text-right font-medium" data-id="element-4542">
                        ${product.forecastRevenue.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-4543">
                        ${product.avgRecurring.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 text-sm text-[#475569] text-right" data-id="element-4544">
                        ${product.avgLTV.toFixed(2)}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>
        </div> : activeTab === 'Retention Health' ? <div className="space-y-6" data-id="element-4545">
          {/* Retention Health Tab Content */}

          {/* Retention Score Card */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-4546">
            <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-4547">
              {/* Left: Gauge */}
              <div className="flex items-center gap-6" data-id="element-4548">
                <div className="relative w-32 h-32 flex items-center justify-center" data-id="element-4549">
                  {/* Background Ring */}
                  <svg className="w-full h-full transform -rotate-90" data-id="element-4550">
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#F1F5F9" strokeWidth="12" data-id="element-4551" />
                    {/* Foreground Ring (78%) */}
                    <circle cx="64" cy="64" r="56" fill="none" stroke="#0EA5E9" strokeWidth="12" strokeDasharray={`${2 * Math.PI * 56}`} strokeDashoffset={`${2 * Math.PI * 56 * (1 - 0.78)}`} strokeLinecap="round" data-id="element-4552" />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center" data-id="element-4553">
                    <span className="text-4xl font-bold text-[#0F172A]" data-id="element-4554">
                      78
                    </span>
                  </div>
                </div>
                <div data-id="element-4555">
                  <div className="text-sm font-medium text-[#64748B] uppercase tracking-wider mb-1" data-id="element-4556">
                    Retention Score
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-[#D1FAE5] text-[#065F46] text-xs font-medium rounded-full w-fit" data-id="element-4557">
                    <ArrowUp className="w-3 h-3" data-id="element-4558" />
                    Improving
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden md:block w-px h-24 bg-[#E2E8F0]" data-id="element-4559" />

              {/* Right: Sparkline */}
              <div className="flex-1 w-full" data-id="element-4560">
                <div className="h-20 w-full" data-id="element-4561">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-4562">
                    <LineChart data={retentionScoreData} data-id="element-4563">
                      <Line type="monotone" dataKey="score" stroke="#0EA5E9" strokeWidth={2} dot={false} data-id="element-4564" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <p className="text-sm text-[#94A3B8] mt-2" data-id="element-4565">
                  Composite score based on churn rate, member tenure,
                  engagement, and cohort performance.
                </p>
              </div>
            </div>
          </div>

          {/* Factor Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-4566">
            {/* Card 1: Churn Rate */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-4567">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#EF4444]" data-id="element-4568" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-4569">
                Churn Rate
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-4570">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-4571">4.2%</span>
                <ArrowDown className="w-4 h-4 text-[#EF4444]" data-id="element-4572" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-4573">vs 3.8% last month</div>
            </div>

            {/* Card 2: Avg Tenure */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-4574">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#10B981]" data-id="element-4575" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-4576">
                Avg Tenure
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-4577">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-4578">14.2</span>
                <span className="text-sm text-[#64748B]" data-id="element-4579">months</span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-4580" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-4581">vs 13.1 last month</div>
            </div>

            {/* Card 3: Engagement Rate */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-4582">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#10B981]" data-id="element-4583" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-4584">
                Engagement Rate
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-4585">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-4586">62%</span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-4587" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-4588">vs 58% last month</div>
            </div>

            {/* Card 4: Cohort Quality */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4 relative overflow-hidden" data-id="element-4589">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#94A3B8]" data-id="element-4590" />
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-4591">
                Cohort Quality
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-4592">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-4593">Good</span>
                <Minus className="w-4 h-4 text-[#94A3B8]" data-id="element-4594" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-4595">Stable vs last month</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4596">
            {/* Tenure Distribution */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-4597">
              <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-4598">
                Tenure Distribution
              </h3>
              <div className="h-[300px] w-full" data-id="element-4599">
                <ResponsiveContainer width="100%" height="100%" data-id="element-4600">
                  <BarChart data={tenureDistributionData} margin={{
                top: 20,
                right: 30,
                left: 0,
                bottom: 5
              }} data-id="element-4601">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4602" />
                    <XAxis dataKey="range" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} dy={10} data-id="element-4603" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} data-id="element-4604" />
                    <Tooltip cursor={{
                  fill: '#F1F5F9'
                }} contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px'
                }} data-id="element-4605" />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} data-id="element-4606">
                      {tenureDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.range === '3+y' ? '#0284C7' : '#0EA5E9'} data-id="element-4607" />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 flex justify-end" data-id="element-4608">
                <div className="flex items-center gap-2 text-xs text-[#64748B]" data-id="element-4609">
                  <div className="w-3 h-3 bg-[#0284C7] rounded-sm" data-id="element-4610"></div>
                  <span data-id="element-4611">Your most loyal (3+ years)</span>
                </div>
              </div>
            </div>

            {/* Churn Waterfall */}
            <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-4612">
              <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-4613">
                Churn Waterfall (Current Month)
              </h3>
              <div className="h-[300px] w-full" data-id="element-4614">
                <ResponsiveContainer width="100%" height="100%" data-id="element-4615">
                  <BarChart data={waterfallChartData} margin={{
                top: 30,
                right: 30,
                left: 0,
                bottom: 5
              }} barCategoryGap="20%" data-id="element-4616">
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4617" />
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 11
                }} dy={10} data-id="element-4618" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fill: '#64748B',
                  fontSize: 12
                }} domain={[328, 372]} ticks={[330, 340, 350, 360, 370]} allowDataOverflow={true} data-id="element-4619" />
                    <Tooltip cursor={{
                  fill: '#F1F5F9'
                }} contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px'
                }} formatter={(_value: any, name: string, props: any) => {
                  if (name === 'placeholder') return [null, null];
                  return [props.payload.label, props.payload.name];
                }} data-id="element-4620" />
                    <Bar dataKey="placeholder" stackId="a" fill="transparent" isAnimationActive={false} data-id="element-4621">
                      {waterfallChartData.map((_entry, index) => <Cell key={`ph-${index}`} fill="transparent" data-id="element-4622" />)}
                    </Bar>
                    <Bar dataKey="value" stackId="a" radius={[4, 4, 0, 0]} isAnimationActive={false} label={{
                  content: (props: any) => {
                    const {
                      x,
                      y,
                      width,
                      index
                    } = props;
                    const item = waterfallChartData[index];
                    if (!item) return null;
                    return <text x={x + width / 2} y={y - 8} textAnchor="middle" fill={item.color === '#EF4444' ? '#EF4444' : item.color === '#10B981' ? '#10B981' : '#0EA5E9'} fontSize={12} fontWeight={600} data-id="element-4624">
                              {item.label}
                            </text>;
                  }
                }} data-id="element-4623">
                      {waterfallChartData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4625" />)}
                    </Bar>
                    <Customized component={(props: any) => {
                  const {
                    formattedGraphicalItems
                  } = props;
                  if (!formattedGraphicalItems || formattedGraphicalItems.length < 2) return null;
                  const valueBarItems = formattedGraphicalItems[1];
                  if (!valueBarItems?.props?.data) return null;
                  const bars = valueBarItems.props.data;
                  const lines: React.ReactElement[] = [];
                  for (let i = 0; i < bars.length - 1; i++) {
                    const currentBar = bars[i];
                    const nextBar = bars[i + 1];
                    if (!currentBar || !nextBar) continue;
                    const item = waterfallChartData[i];
                    const isNegative = item.color === '#EF4444';
                    // For positive/total bars, connector at top (y)
                    // For negative bars, connector at bottom (y + height)
                    let connectorY: number;
                    if (isNegative) {
                      connectorY = currentBar.y + currentBar.height;
                    } else {
                      connectorY = currentBar.y;
                    }
                    const x1 = currentBar.x + currentBar.width;
                    const x2 = nextBar.x;
                    lines.push(<line key={`connector-${i}`} x1={x1} y1={connectorY} x2={x2} y2={connectorY} stroke="#CBD5E1" strokeWidth={1} strokeDasharray="4 3" data-id="element-4627" />);
                  }
                  return <g data-id="element-4628">{lines}</g>;
                }} data-id="element-4626" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-4 text-center" data-id="element-4629">
                <span className="text-sm font-medium text-[#10B981]" data-id="element-4630">
                  Net change: +6 members this month
                </span>
              </div>
            </div>
          </div>

          {/* Retention Curve Summary */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] p-6 shadow-sm" data-id="element-4631">
            <div className="flex items-center justify-between mb-6" data-id="element-4632">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-4633">
                Retention Curve Summary
              </h3>
              <div className="flex items-center gap-4 text-sm" data-id="element-4634">
                <div className="flex items-center gap-2" data-id="element-4635">
                  <div className="w-3 h-1 bg-[#0EA5E9]" data-id="element-4636"></div>
                  <span className="text-[#0F172A]" data-id="element-4637">Your Club</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-4638">
                  <div className="w-3 h-1 border-t border-dashed border-[#94A3B8]" data-id="element-4639"></div>
                  <span className="text-[#64748B]" data-id="element-4640">Industry Average</span>
                </div>
              </div>
            </div>
            <div className="h-[300px] w-full" data-id="element-4641">
              <ResponsiveContainer width="100%" height="100%" data-id="element-4642">
                <LineChart data={retentionCurveData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-4643">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4644" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} data-id="element-4645" />
                  <YAxis axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} tickFormatter={val => `${val}%`} domain={[0, 100]} data-id="element-4646" />
                  <Tooltip contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px'
              }} formatter={(val: number) => [`${val}%`, '']} data-id="element-4647" />
                  <Line type="monotone" dataKey="club" name="Your Club" stroke="#0EA5E9" strokeWidth={2} dot={{
                r: 4,
                fill: '#0EA5E9',
                strokeWidth: 2,
                stroke: '#fff'
              }} activeDot={{
                r: 6
              }} data-id="element-4648" />
                  <Line type="monotone" dataKey="industry" name="Industry Average" stroke="#94A3B8" strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={{
                r: 6
              }} data-id="element-4649" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 flex justify-end" data-id="element-4650">
              <button onClick={() => navigate('/dashboard/retention')} className="flex items-center gap-1 text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7] transition-colors" data-id="element-4651">
                View full cohort analysis <ArrowRight className="w-4 h-4" data-id="element-4652" />
              </button>
            </div>
          </div>

          {/* Insight Card */}
          <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-4 flex items-start gap-3" data-id="element-4653">
            <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-4654" />
            <p className="text-sm text-[#92400E]" data-id="element-4655">
              Your 6-12 month members are your highest risk cohort this quarter.
              Consider a re-engagement campaign targeting members approaching
              their first anniversary.
            </p>
          </div>
        </div> : <div className="space-y-6" data-id="element-4656">
          {/* Wine Journey Tab Content */}

          {/* Metric Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-4657">
            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4" data-id="element-4658">
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-4659">
                Varietal Diversity Score
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-4660">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-4661">
                  6.4 / 10
                </span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-4662" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-4663">vs 5.8 last quarter</div>
            </div>

            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4" data-id="element-4664">
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-4665">
                Discovery Rate
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-4666">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-4667">34%</span>
                <ArrowUp className="w-4 h-4 text-[#10B981]" data-id="element-4668" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-4669">vs 28% last quarter</div>
            </div>

            <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-4" data-id="element-4670">
              <div className="text-sm font-medium text-[#64748B] mb-1" data-id="element-4671">
                Avg Varietals per Member
              </div>
              <div className="flex items-baseline gap-2 mb-1" data-id="element-4672">
                <span className="text-2xl font-bold text-[#0F172A]" data-id="element-4673">4.2</span>
                <Minus className="w-4 h-4 text-[#94A3B8]" data-id="element-4674" />
              </div>
              <div className="text-xs text-[#94A3B8]" data-id="element-4675">
                Stable vs last quarter
              </div>
            </div>
          </div>

          {/* Product Lifecycle Map - Full Width */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-4676">
            <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-4677">
              Product Lifecycle Map
            </h3>
            <div className="h-[350px] w-full" data-id="element-4678">
              <ResponsiveContainer width="100%" height="100%" data-id="element-4679">
                <ScatterChart margin={{
              top: 20,
              right: 60,
              bottom: 20,
              left: 0
            }} data-id="element-4680">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4681" />
                  <XAxis type="number" dataKey="tenure" name="Tenure" unit="m" domain={[0, 24]} tick={{
                fill: '#64748B',
                fontSize: 12
              }} label={{
                value: 'Avg Member Tenure (Months)',
                position: 'bottom',
                offset: 0,
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-4682" />
                  <YAxis type="number" dataKey="repeatRate" name="Repeat Rate" unit="%" domain={[0, 100]} tick={{
                fill: '#64748B',
                fontSize: 12
              }} label={{
                value: 'Repeat Rate (%)',
                angle: -90,
                position: 'insideLeft',
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-4683" />
                  <ZAxis type="number" dataKey="units" range={[100, 1000]} name="Units" data-id="element-4684" />
                  <Tooltip cursor={{
                strokeDasharray: '3 3'
              }} content={({
                active,
                payload
              }) => {
                if (active && payload && payload.length) {
                  const data = payload[0].payload;
                  return <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 shadow-lg" data-id="element-4686">
                            <p className="font-medium text-[#0F172A] mb-1" data-id="element-4687">
                              {data.name}
                            </p>
                            <p className="text-xs text-[#64748B]" data-id="element-4688">
                              Tenure: {data.tenure} months
                            </p>
                            <p className="text-xs text-[#64748B]" data-id="element-4689">
                              Repeat Rate: {data.repeatRate}%
                            </p>
                            <p className="text-xs text-[#64748B]" data-id="element-4690">
                              Units: {data.units}
                            </p>
                          </div>;
                }
                return null;
              }} data-id="element-4685" />
                  <ReferenceArea x1={0} x2={6} y1={0} y2={40} fill="#E0F2FE" fillOpacity={0.3} stroke="none" label={{
                value: 'Gateway Wines',
                position: 'insideBottomLeft',
                fill: '#0369A1',
                fontSize: 12
              }} data-id="element-4691" />
                  <ReferenceArea x1={12} x2={24} y1={60} y2={100} fill="#DCFCE7" fillOpacity={0.3} stroke="none" label={{
                value: 'Loyalty Wines',
                position: 'insideTopRight',
                fill: '#15803D',
                fontSize: 12
              }} data-id="element-4692" />
                  <Scatter name="Products" data={productLifecycleData} data-id="element-4693">
                    {productLifecycleData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-4694" />)}
                  </Scatter>
                  <Customized component={(props: any) => {
                const {
                  xAxisMap,
                  yAxisMap
                } = props;
                if (!xAxisMap || !yAxisMap) return null;
                const xAxis = Object.values(xAxisMap)[0] as any;
                const yAxis = Object.values(yAxisMap)[0] as any;
                if (!xAxis?.scale || !yAxis?.scale) return null;
                const labelOffsets: Record<string, {
                  dx: number;
                  dy: number;
                }> = {
                  '2024 Rosé': {
                    dx: 12,
                    dy: 4
                  },
                  '2024 Sauv Blanc': {
                    dx: 14,
                    dy: 4
                  },
                  '2023 Shiraz': {
                    dx: 16,
                    dy: -6
                  },
                  '2023 Reserve Cab': {
                    dx: 14,
                    dy: 4
                  },
                  '2024 Pinot Noir': {
                    dx: 14,
                    dy: -8
                  },
                  '2023 Sparkling': {
                    dx: 12,
                    dy: -8
                  }
                };
                return <g data-id="element-4696">
                          {productLifecycleData.map((item, i) => {
                    const cx = xAxis.scale(item.tenure);
                    const cy = yAxis.scale(item.repeatRate);
                    const offset = labelOffsets[item.name] || {
                      dx: 12,
                      dy: 4
                    };
                    return <text key={`label-${i}`} x={cx + offset.dx} y={cy + offset.dy} fill="#94A3B8" fontSize={10} textAnchor="start" dominantBaseline="middle" data-id="element-4697">
                                {item.name}
                              </text>;
                  })}
                        </g>;
              }} data-id="element-4695" />
                </ScatterChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Swap & Drop Patterns - Full Width */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-4698">
            <div className="flex items-center justify-between mb-6" data-id="element-4699">
              <h3 className="text-base font-semibold text-[#0F172A]" data-id="element-4700">
                Swap & Drop Patterns
              </h3>
              <div className="flex items-center gap-4 text-xs" data-id="element-4701">
                <div className="flex items-center gap-1.5" data-id="element-4702">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#0EA5E9]" data-id="element-4703" />
                  <span className="text-[#64748B]" data-id="element-4704">Swapped In</span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-4705">
                  <div className="w-2.5 h-2.5 rounded-full bg-[#F87171]" data-id="element-4706" />
                  <span className="text-[#64748B]" data-id="element-4707">Dropped</span>
                </div>
              </div>
            </div>
            <div className="h-[280px] w-full" data-id="element-4708">
              <ResponsiveContainer width="100%" height="100%" data-id="element-4709">
                <BarChart layout="vertical" data={swapDropData} margin={{
              top: 5,
              right: 30,
              left: 40,
              bottom: 5
            }} data-id="element-4710">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" data-id="element-4711" />
                  <XAxis type="number" hide data-id="element-4712" />
                  <YAxis type="category" dataKey="name" tick={{
                fill: '#64748B',
                fontSize: 12
              }} width={120} axisLine={false} tickLine={false} data-id="element-4713" />
                  <Tooltip cursor={{
                fill: '#F1F5F9'
              }} contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px'
              }} data-id="element-4714" />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={24} data-id="element-4715">
                    {swapDropData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.type === 'in' ? '#0EA5E9' : '#F87171'} data-id="element-4716" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Varietal Diversity Over Time */}
          <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-sm p-6" data-id="element-4717">
            <h3 className="text-base font-semibold text-[#0F172A] mb-6" data-id="element-4718">
              Varietal Diversity Over Time
            </h3>
            <div className="h-[300px] w-full" data-id="element-4719">
              <ResponsiveContainer width="100%" height="100%" data-id="element-4720">
                <AreaChart data={varietalDiversityData} margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0
            }} stackOffset="expand" data-id="element-4721">
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" data-id="element-4722" />
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} dy={10} data-id="element-4723" />
                  <YAxis tickFormatter={val => `${(val * 100).toFixed(0)}%`} axisLine={false} tickLine={false} tick={{
                fill: '#64748B',
                fontSize: 12
              }} data-id="element-4724" />
                  <Tooltip contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E2E8F0',
                borderRadius: '8px'
              }} formatter={(value: number, name: string) => {
                const nameMap: Record<string, string> = {
                  shiraz: 'Shiraz',
                  cabernet: 'Cabernet',
                  pinotNoir: 'Pinot Noir',
                  chardonnay: 'Chardonnay',
                  sauvBlanc: 'Sauvignon Blanc',
                  rose: 'Rosé',
                  sparkling: 'Sparkling',
                  other: 'Other'
                };
                return [`${value}%`, nameMap[name] || name];
              }} data-id="element-4725" />
                  <Area type="monotone" dataKey="shiraz" stackId="1" stroke="#7F1D1D" fill="#991B1B" name="shiraz" data-id="element-4726" />
                  <Area type="monotone" dataKey="cabernet" stackId="1" stroke="#991B1B" fill="#B91C1C" name="cabernet" data-id="element-4727" />
                  <Area type="monotone" dataKey="pinotNoir" stackId="1" stroke="#B91C1C" fill="#DC2626" name="pinotNoir" data-id="element-4728" />
                  <Area type="monotone" dataKey="chardonnay" stackId="1" stroke="#D97706" fill="#F59E0B" name="chardonnay" data-id="element-4729" />
                  <Area type="monotone" dataKey="sauvBlanc" stackId="1" stroke="#059669" fill="#10B981" name="sauvBlanc" data-id="element-4730" />
                  <Area type="monotone" dataKey="rose" stackId="1" stroke="#DB2777" fill="#EC4899" name="rose" data-id="element-4731" />
                  <Area type="monotone" dataKey="sparkling" stackId="1" stroke="#0284C7" fill="#38BDF8" name="sparkling" data-id="element-4732" />
                  <Area type="monotone" dataKey="other" stackId="1" stroke="#475569" fill="#94A3B8" name="other" data-id="element-4733" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4" data-id="element-4734">
              {[{
            label: 'Shiraz',
            color: '#991B1B'
          }, {
            label: 'Cabernet',
            color: '#B91C1C'
          }, {
            label: 'Pinot Noir',
            color: '#DC2626'
          }, {
            label: 'Chardonnay',
            color: '#F59E0B'
          }, {
            label: 'Sauvignon Blanc',
            color: '#10B981'
          }, {
            label: 'Rosé',
            color: '#EC4899'
          }, {
            label: 'Sparkling',
            color: '#38BDF8'
          }, {
            label: 'Other',
            color: '#94A3B8'
          }].map(item => <div key={item.label} className="flex items-center gap-1.5" data-id="element-4735">
                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{
              backgroundColor: item.color
            }} data-id="element-4736" />
                  <span className="text-xs text-[#64748B]" data-id="element-4737">{item.label}</span>
                </div>)}
            </div>
            <p className="text-sm text-[#94A3B8] mt-3" data-id="element-4738">
              A broadening mix indicates healthy exploration. A narrowing mix
              may signal member fatigue.
            </p>
          </div>

          {/* Insight Card */}
          <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-4 flex items-start gap-3" data-id="element-4739">
            <Lightbulb className="w-5 h-5 text-[#D97706] flex-shrink-0 mt-0.5" data-id="element-4740" />
            <p className="text-sm text-[#92400E]" data-id="element-4741">
              Your 2024 Rosé has a high gateway rate but very low repeat
              purchases. Consider featuring it in new member welcome boxes while
              offering Pinot Noir as the step-up recommendation.
            </p>
          </div>
        </div>}
    </div>;
}